import { get, isNumber } from 'lodash';
import { Framework } from '@/framework/instance';
import { applyDifferentProps } from '@/utils/layer-kit';
import { IEditMessage } from '../../types';
import { OperateType } from '../../types/operate';
import { parseWsKey } from '../../utils';
import { Collaborators } from '../collaborators';

interface IRoom {
    onMessage: (type: OperateType, cb: (data: IEditMessage<any>) => void) => void;
    onTick: (cb: () => void) => number;
    isRealEdit: () => boolean;
    sendToAll: (type: OperateType, data?: unknown) => Promise<void>;
    collaborators: Collaborators;
}

const baseSyncAttrs = ['width', 'height', 'left', 'top', 'rotate'];

/** 同步基础信息适配器 */
export class SyncBaseOperateAdapter extends Framework {
    private operateInfoMap: Map<string, { time: number; wsKeys: Set<number>; syncAttrs: Set<string> }> = new Map();

    constructor(
        identity: string,
        private room: IRoom,
    ) {
        super(identity);
        this.room.onTick(this.onTick.bind(this));
        room.onMessage(OperateType.SyncBaseOperate, this.onReceive.bind(this));
    }

    set(wsKey: string | number, layerId: string, syncAttrs?: string[]) {
        if (this.room.isRealEdit()) {
            const info = this.operateInfoMap.get(layerId) || {
                time: Date.now(),
                wsKeys: new Set(),
                syncAttrs: new Set(),
            };
            if (isNumber(wsKey)) {
                info.wsKeys.add(wsKey);
            } else {
                info.wsKeys.add(parseWsKey(wsKey).wsKey);
            }
            (syncAttrs || []).concat(...baseSyncAttrs).forEach((attr) => {
                info.syncAttrs.add(attr);
            });
            info.time = Date.now();
            this.operateInfoMap.set(layerId, info);
        } else {
            this.operateInfoMap.delete(layerId);
        }
        // console.log('ot 设置定时同步数据', layerId, Array.from(this.operateInfoMap.keys()));
    }

    onTick() {
        const layerIds = Array.from(this.operateInfoMap.keys());
        for (const layerId of layerIds) {
            const info = this.operateInfoMap.get(layerId);
            if (info && Date.now() - info.time > 1500) {
                this.operateInfoMap.delete(layerId);
                if (info.wsKeys.size > 1) {
                    const wsKeys = Array.from(info.wsKeys).sort((a, b) => a - b);
                    for (const wsKey of wsKeys) {
                        const member = this.room.collaborators.getMember(wsKey);
                        if (!member) continue;
                        if (this.room.collaborators.getWsKey() === wsKey) {
                            const latestLayer = this.framework.getLayer(layerId);
                            if (latestLayer) {
                                const diffInfo = {};
                                info.syncAttrs.forEach((attr) => {
                                    // 如果attr是data.anchor，需要设置成 diffInfo.data.anchor=get(latestLayer, attr);
                                    if (attr.includes('.')) {
                                        // 处理嵌套属性，如 'data.anchor'
                                        const pathParts = attr.split('.');
                                        let current = diffInfo;
                                        // 创建嵌套对象结构
                                        for (let i = 0; i < pathParts.length - 1; i++) {
                                            const part = pathParts[i];
                                            if (!current[part]) {
                                                current[part] = {};
                                            }
                                            current = current[part];
                                        }
                                        // 设置最终值
                                        const lastPart = pathParts[pathParts.length - 1];
                                        current[lastPart] = get(latestLayer, attr);
                                    } else {
                                        // 处理普通属性
                                        diffInfo[attr] = get(latestLayer, attr);
                                    }
                                });
                                console.log('ot 定时同步', layerId, diffInfo);
                                this.room.sendToAll(OperateType.SyncBaseOperate, [{ [layerId]: diffInfo }]);
                            }
                        }
                    }
                }
            }
        }

        // console.log('ot sync base tick', Array.from(this.operateInfoMap.keys()));
    }

    private onReceive({ data }: IEditMessage<[Record<string, any>]>) {
        const [diffInfo] = data;
        console.log('ot 收到定时同步', diffInfo);
        Object.keys(diffInfo).forEach((layerId) => {
            this.operateInfoMap.delete(layerId);
            const differentProps = diffInfo[layerId];
            const layer = this.framework.getLayer(layerId, true);
            if (!layer) return;
            const hasChanges = applyDifferentProps(layer, differentProps);
            if (hasChanges) {
                this.framework.stage.addOrUpdate(layer);
                this.framework.operate.tryRefreshActiveLayer(layerId);
            }
            console.log('ot 收到定时同步处理完成', layerId, hasChanges, differentProps);
        });
    }
}
